import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { FileText, User, Key, LogOut, UserCog, Bell } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function DashboardPersona() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    axios.get(`${API}/evaluaciones/mis-notificaciones`, {
      headers: { Authorization: `Bearer ${token}` },
    }).then(({ data }) => {
      setUnreadCount(data.unread_count || 0);
    }).catch(() => {});
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    {
      icon: FileText,
      title: 'Postularse',
      description: 'Ver convocatorias disponibles y postularse',
      onClick: () => navigate('/convocatorias-disponibles'),
      highlight: true
    },
    {
      icon: User,
      title: 'Mis Postulaciones',
      description: 'Ver el estado de mis postulaciones',
      onClick: () => navigate('/mis-postulaciones')
    },
    {
      icon: Bell,
      title: 'Mis Notificaciones',
      description: 'Alertas y decisiones del evaluador sobre tus documentos',
      onClick: () => navigate('/mis-notificaciones'),
      badge: unreadCount,
    },
    {
      icon: UserCog,
      title: 'Editar mi perfil',
      description: 'Actualizar información personal',
      onClick: () => navigate('/perfil/editar')
    },
    {
      icon: Key,
      title: 'Cambiar contraseña',
      description: 'Actualizar tu contraseña',
      onClick: () => navigate('/forgot-password')
    },
    {
      icon: LogOut,
      title: 'Salir',
      description: 'Cerrar sesión',
      onClick: handleLogout
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Header */}
      <header className="bg-[#0F172A] border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <img 
              src="https://customer-assets.emergentagent.com/job_login-verify-system/artifacts/gyieqwft_Captura%20de%20Pantalla%202025-12-10%20a%20la%28s%29%207.03.41%20a.%C2%A0m..png" 
              alt="Logo" 
              className="h-12 w-auto object-contain brightness-0 invert"
            />
            <div className="border-l border-slate-600 pl-4">
              <h1 className="text-xl font-semibold text-white">
                Mi Portal
              </h1>
              <p className="text-sm text-slate-300">
                {user?.user_type === 'juridica' 
                  ? user?.company_name 
                  : `${user?.first_name} ${user?.first_lastname}`}
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="space-y-8">
          {/* Welcome Section */}
          <div className="bg-white rounded-lg p-8 shadow-sm border border-slate-200">
            <h2 className="text-3xl font-bold text-slate-900 mb-2">
              Bienvenido, {user?.user_type === 'juridica' ? user?.company_name : user?.first_name}
            </h2>
            <p className="text-lg text-slate-600">
              Explora las convocatorias disponibles y postúlate a las que se ajusten a tu perfil
            </p>
          </div>

          {/* Menu Grid */}
          <div className="grid grid-cols-1 gap-4">
            {menuItems.map((item, index) => {
              const Icon = item.icon;
              const isHighlight = item.highlight;
              const isLast = item.title === 'Salir';
              
              return (
                <button
                  key={index}
                  onClick={item.onClick}
                  className={`
                    w-full text-left p-6 rounded-lg border transition-all hover:shadow-md
                    ${isHighlight 
                      ? 'bg-blue-50 border-blue-300 hover:bg-blue-100 hover:border-blue-400' 
                      : isLast
                      ? 'bg-white border-slate-200 hover:bg-slate-50'
                      : 'bg-white border-slate-200 hover:bg-slate-50'
                    }
                  `}
                >
                  <div className="flex items-start gap-4">
                    <div className={`
                      p-3 rounded-lg
                      ${isHighlight 
                        ? 'bg-blue-100' 
                        : 'bg-slate-100'
                      }
                    `}>
                      <Icon className={`
                        h-6 w-6
                        ${isHighlight 
                          ? 'text-blue-700' 
                          : 'text-slate-700'
                        }
                      `} />
                    </div>
                    <div className="flex-1">
                      <h3 className={`
                        text-lg font-semibold mb-1 flex items-center gap-2
                        ${isHighlight 
                          ? 'text-blue-900' 
                          : 'text-slate-900'
                        }
                      `}>
                        {item.title}
                        {item.badge > 0 && (
                          <span
                            data-testid="notif-badge"
                            className="inline-flex items-center justify-center min-w-[22px] h-[22px] px-1.5 rounded-full bg-red-500 text-white text-xs font-bold"
                          >
                            {item.badge > 99 ? '99+' : item.badge}
                          </span>
                        )}
                      </h3>
                      <p className="text-sm text-slate-600">
                        {item.description}
                      </p>
                    </div>
                    <div className="flex items-center">
                      <svg 
                        className={`h-5 w-5 ${isHighlight ? 'text-blue-500' : 'text-slate-400'}`}
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </main>
    </div>
  );
}
